/**
 * ScheduleMessageModal - Modal for scheduling a follow-up message.
 *
 * Two modes:
 *   - "single" (default, original behaviour): schedules for one contact, via
 *     followupService.quickCreate.
 *   - "label_broadcast": campaign dispatch to every contact attached to a
 *     label, via labelDispatchService.dispatch. Reuses the same UI for
 *     date/time/recurrence so the operator's mental model stays the same.
 *     A channel selector (wa_personal | wa_business | both) replaces the
 *     contact line in the header.
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  X, Clock, Loader2, Check, Calendar, Repeat, Paperclip,
  Image, Film, Music, FileText, Trash2, Lock, Zap, Users,
  Sparkles, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp,
  ShieldCheck, Send,
} from 'lucide-react';
import followupService from '../../services/global/followupService';
import labelDispatchService from '../../services/global/labelDispatchService';
import fileUploadService from '../../services/global/fileUploadService';
import subscriptionService from '../../services/global/subscriptionService';
import templateService from '../../services/global/templateService';
import {
  templateRequiresParams, positionalParamsInText, fillBodyText,
} from '../../utils/templateVariables';
import PromoModal from '../PromoModal';
import useActiveConnections from '../../hooks/useActiveConnections';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import BusinessWindowFields from '../common/BusinessWindowFields';
import VariableChips from '../messages/VariableChips';
import TemplateMessageField from '../messages/TemplateMessageField';
import TemplatePicker from '../messages/TemplatePicker';
import {
  analyzeTemplate, findTemplate, uniqueApproved, templateCapabilities, templateUsability,
  missingValues, automationTemplateFields, emptyTemplateValues,
} from '../../utils/templateParams';

const WEEKDAYS = [
  { value: 0, label: 'Seg' },
  { value: 1, label: 'Ter' },
  { value: 2, label: 'Qua' },
  { value: 3, label: 'Qui' },
  { value: 4, label: 'Sex' },
  { value: 5, label: 'Sáb' },
  { value: 6, label: 'Dom' },
];

const FILE_ICONS = {
  image: Image,
  video: Film,
  audio: Music,
  pdf: FileText,
  document: FileText,
};

/** Inputs dos {{n}} do template principal + prévia (compartilhado entre o
 *  modo single e o broadcast do modal). */
const TemplateParamsFields = ({ params, values, onChange, bodyText, colors }) => {
  if (!params.length) return null;
  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {params.map((n, i) => (
        <input
          key={n}
          value={values[i] || ''}
          onChange={(e) => {
            const next = [...values];
            next[i] = e.target.value;
            onChange(next);
          }}
          placeholder={`Valor do {{${n}}} — ex.: ${n === 1 ? '{{primeiro_nome}}' : 'texto fixo ou {{campo:Nome}}'}`}
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 8,
            border: `1px solid ${colors.borderColor}`,
            backgroundColor: colors.bgPrimary, color: colors.textPrimary,
            fontSize: 13, outline: 'none', boxSizing: 'border-box',
          }}
        />
      ))}
      <div style={{ fontSize: 11, color: colors.textTertiary }}>
        Aceita {'{{primeiro_nome}}'}, {'{{campo:Nome}}'} e afins, resolvidos por
        destinatário na hora do envio.
      </div>
      <div style={{
        fontSize: 12, color: colors.textPrimary, whiteSpace: 'pre-wrap',
        backgroundColor: colors.bgSecondary, borderRadius: 8, padding: '8px 10px',
        border: `1px solid ${colors.borderColor}`, lineHeight: 1.45,
      }}>
        {fillBodyText(bodyText, values)}
      </div>
    </div>
  );
};

const ScheduleMessageModal = ({
  isOpen,
  onClose,
  // single-mode props
  contactPhone,
  contactName,
  // label_broadcast-mode props
  mode = 'single',
  labelInfo, // { id, name, color }
  // shared
  colors,
  onScheduled,
  channel,
  // 'modal' (default) ou 'drawer' (painel lateral direito — passado pelos
  // ChatWindows para alinhar com o ContactDetailsDrawer).
  displayMode = 'modal',
}) => {
  const isBroadcast = mode === 'label_broadcast';
  const isDrawer = displayMode === 'drawer';

  const [messageText, setMessageText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sendDate, setSendDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [sendTime, setSendTime] = useState('09:00');
  const [recurrenceType, setRecurrenceType] = useState('once'); // 'once' | 'recurring'
  // Cancela o envio agendado se o cliente responder antes (Etapa 4 — A5).
  // A UI so aparece com a flag ligada (backend /api/system/features).
  const [cancelOnReply, setCancelOnReply] = useState(false);
  // Janela comercial (flag automation_business_window): só no expediente e
  // só nestes dias (0=Seg..6=Dom, mesmo formato do recurrence_days).
  const [businessWindow, setBusinessWindow] = useState({ business_hours_only: false, weekdays: [] });
  const [recurrence, setRecurrence] = useState('daily'); // sub-option when recurring
  const [recurrenceDays, setRecurrenceDays] = useState([]);
  const [recurrenceDayOfMonth, setRecurrenceDayOfMonth] = useState(1);
  const [scheduling, setScheduling] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Plan limits state
  const [limits, setLimits] = useState(null);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoPlans, setPromoPlans] = useState([]);
  const [promoSubtitle, setPromoSubtitle] = useState('');

  // Broadcast-only state
  // O canal e detectado automaticamente das conexoes do usuario; nao
  // perguntamos no modal porque a info ja existe na plataforma desde o
  // onboarding. Quando o usuario tem ambos, mandamos "both" e o backend
  // escolhe por contato. Para nao quebrar a chave de useEffect existente
  // mantemos o estado, mas ele e SO DERIVADO do hook abaixo.
  const connections = useActiveConnections({ enabled: isOpen && mode === 'label_broadcast' });
  const { flags: featureFlags } = useFeatureFlags();
  // Seletor único e editor de variáveis (2026-09-14). Sem a flag, como antes.
  const templateUi = Boolean(featureFlags.template_params_ui);
  const scheduleCaps = templateCapabilities('schedule', featureFlags);
  const fallbackCaps = templateCapabilities('fallback', featureFlags);
  const broadcastChannel = connections.channelFilter || 'both';
  const [recipientPreview, setRecipientPreview] = useState(null); // { count, sample }
  const [previewLoading, setPreviewLoading] = useState(false);

  // Phase 1.A — immediate dispatch + QR anti-block + HSM fallback.
  // "now" envia imediatamente sem precisar de data/hora/recorrencia; "schedule"
  // mantem o fluxo legado (escolhe data, hora e frequencia).
  const [dispatchTiming, setDispatchTiming] = useState('now');
  const [antiBlock, setAntiBlock] = useState(true);
  const [typingSimulation, setTypingSimulation] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [useTemplateFallback, setUseTemplateFallback] = useState(false);
  const [fallbackTemplateName, setFallbackTemplateName] = useState('');
  const [fallbackTemplateLanguage, setFallbackTemplateLanguage] = useState('pt_BR');
  // Templates aprovados do WA Business para o seletor de "mensagem padrao".
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // ai_generated state (single mode only in V1)
  const [messageType, setMessageType] = useState('custom'); // 'custom' | 'ai_generated' | 'template'
  const [instruction, setInstruction] = useState('');
  // Template Meta como mensagem PRINCIPAL (so wa_business). Distinto do
  // fallbackTemplate, que e enviado quando a mensagem livre/IA nao puder.
  const [mainTemplateName, setMainTemplateName] = useState('');
  const [mainTemplateLanguage, setMainTemplateLanguage] = useState('pt_BR');
  // Valores dos {{n}} do template principal (aceitam {{primeiro_nome}} etc.,
  // resolvidos por destinatário no envio).
  const [mainTemplateParams, setMainTemplateParams] = useState([]);
  const [mainTemplateValues, setMainTemplateValues] = useState(emptyTemplateValues());
  const [useConversationContext, setUseConversationContext] = useState(true);
  // No backend continuam existindo dois flags (allow_agent_skip e
  // force_response). Para o operador leigo expomos apenas "Forcar IA
  // responder" e derivamos allow_agent_skip = !forceResponse no payload —
  // quando o operador desmarca, a IA ganha autonomia de pular via tool
  // "Ignorar Mensagem" (artifact no_response).
  //
  // Default por MODO (2026-08-06): no DISPARO POR ETIQUETA nasce DESMARCADO —
  // pré-marcado, um disparo em massa "bom dia" atropelou a negociação humana
  // do Luiz (myCar, contato 553187877530): contato com humano deve cair no
  // skip visível ("atendimento humano"), não em fala forçada. No agendamento
  // POR CONTATO segue marcado: quem agenda para o próprio contato quer que
  // saia.
  const [forceResponse, setForceResponse] = useState(mode !== 'label_broadcast');
  const allowAgentSkip = !forceResponse;
  const [windowStatus, setWindowStatus] = useState(null); // { is_open, reason } | null
  const [windowLoading, setWindowLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      followupService.getLimits().then(res => {
        setLimits(res.data || res);
      }).catch(() => {});
    }
  }, [isOpen]);

  // Refresh the recipient counter every time the channel filter changes.
  // The backend endpoint is cheap (single ARRAY_CONTAINS query per channel)
  // and the operator wants instant feedback to know how many contacts will
  // actually be hit before they confirm.
  useEffect(() => {
    if (!isOpen || !isBroadcast || !labelInfo?.id) return;
    let cancelled = false;
    setPreviewLoading(true);
    labelDispatchService.previewRecipients(labelInfo.id, broadcastChannel)
      .then(res => {
        if (cancelled) return;
        const data = res?.data || res;
        setRecipientPreview(data || { count: 0, sample: [] });
      })
      .catch(() => { if (!cancelled) setRecipientPreview({ count: 0, sample: [] }); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, isBroadcast, labelInfo?.id, broadcastChannel]);

  // Carrega templates aprovados do WA Business quando o usuario abre o modal
  // como IA + WA Business — alimenta o seletor de "mensagem padrao" (fallback
  // HSM). Faz dedupe por nome (preferindo APPROVED) e ordena alfabeticamente.
  useEffect(() => {
    if (!isOpen) return;
    const isBusinessContext =
      channel === 'wa_business' ||
      (isBroadcast && (broadcastChannel === 'wa_business' || broadcastChannel === 'both'));
    if (!isBusinessContext) return;
    let cancelled = false;
    setTemplatesLoading(true);
    templateService.list()
      .then(res => {
        if (cancelled) return;
        // Endpoint retorna { templates: [...], count: N } — mesma chave usada
        // por TemplatesPage. Fallbacks cobrem variacoes legadas.
        const list = res?.templates || res?.data || (Array.isArray(res) ? res : []) || [];
        const byName = new Map();
        list.forEach(t => {
          if (!t?.name) return;
          const existing = byName.get(t.name);
          if (!existing || (existing.status !== 'APPROVED' && t.status === 'APPROVED')) {
            byName.set(t.name, t);
          }
        });
        const arr = Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
        setAvailableTemplates(arr);
      })
      .catch(() => { if (!cancelled) setAvailableTemplates([]); })
      .finally(() => { if (!cancelled) setTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, channel, isBroadcast, broadcastChannel]);

  // Probe the WhatsApp Business 24h window when scheduling ai_generated for a
  // contact on wa_business. Only informational — won't block scheduling.
  useEffect(() => {
    if (!isOpen || isBroadcast) return;
    if (messageType !== 'ai_generated') {
      setWindowStatus(null);
      return;
    }
    if (channel !== 'wa_business' || !contactPhone) {
      setWindowStatus(null);
      return;
    }
    let cancelled = false;
    setWindowLoading(true);
    followupService.checkRecipientWindow(contactPhone)
      .then(res => {
        if (cancelled) return;
        const data = res?.data || res;
        setWindowStatus(data);
      })
      .catch(() => { if (!cancelled) setWindowStatus(null); })
      .finally(() => { if (!cancelled) setWindowLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, isBroadcast, messageType, channel, contactPhone]);

  if (!isOpen) return null;

  // Enquanto o limits ainda nao chegou (1a abertura, sem cache), assumimos
  // que pode criar — assim nao mostramos badge PRO falso por 1-3s em contas
  // que tem plano. O backend ainda valida no submit; se for free, retorna
  // 403 e abrimos o promo modal naquele momento.
  const limitsLoaded = limits != null;
  const canCreateRecurring = !limitsLoaded || (limits?.can_create_recurring ?? true);
  const canCreateAiGenerated = !limitsLoaded || (limits?.can_create_ai_generated ?? false);
  const isAtCountLimit = limits?.max_allowed != null && limits.current_count >= limits.max_allowed;

  const effectiveRecurrence = recurrenceType === 'once' ? 'once' : recurrence;
  const isAi = messageType === 'ai_generated';
  const isTemplate = messageType === 'template';
  const isBusinessChannel = channel === 'wa_business';
  const aiInstructionValid = isAi ? (instruction.trim().length >= 10) : true;
  // Fallback automático continua restrito a template SEM variável (não há
  // quem preencha na hora); o template PRINCIPAL aceita variáveis.
  const fallbackTemplates = availableTemplates.filter((t) => !templateRequiresParams(t));
  const selectedMainTemplate = availableTemplates.find((t) => t.name === mainTemplateName);
  const mainTemplateBodyText = (selectedMainTemplate?.components || [])
    .find((c) => (c.type || '').toUpperCase() === 'BODY')?.text || '';
  const mainTemplateBodyParams = positionalParamsInText(mainTemplateBodyText);
  const mainTemplateParamsFilled = mainTemplateBodyParams.every(
    (n, i) => (mainTemplateParams[i] || '').trim()
  );
  const approvedList = uniqueApproved(availableTemplates);
  const newTemplateAnalysis = (() => {
    const tpl = findTemplate(approvedList, mainTemplateName, mainTemplateLanguage);
    return tpl ? analyzeTemplate(tpl) : null;
  })();
  const fallbackUsable = (() => {
    const tpl = findTemplate(approvedList, fallbackTemplateName, fallbackTemplateLanguage);
    return Boolean(tpl) && templateUsability(analyzeTemplate(tpl), fallbackCaps).usable;
  })();
  const templateValid = isTemplate
    ? (templateUi
      ? Boolean(newTemplateAnalysis)
        && missingValues(newTemplateAnalysis, mainTemplateValues, scheduleCaps).length === 0
      : (mainTemplateName.trim().length > 0 && mainTemplateParamsFilled))
    : true;
  const hasContent = isAi
    ? aiInstructionValid
    : isTemplate
      ? templateValid
      : (messageText.trim() || attachedFiles.length > 0);
  // Em broadcast + "agora" o backend reescreve start_date/send_time, entao
  // dispensamos a validacao desses campos no UI.
  const immediateBroadcast = isBroadcast && dispatchTiming === 'now';
  const canSchedule = hasContent &&
    (immediateBroadcast || (sendDate && sendTime)) &&
    (immediateBroadcast || effectiveRecurrence !== 'weekly' || recurrenceDays.length > 0) &&
    (immediateBroadcast || effectiveRecurrence !== 'monthly' || recurrenceDayOfMonth) &&
    // Fallback HSM exige nome do template quando ligado.
    (!useTemplateFallback || (fallbackTemplateName.trim().length > 0)) &&
    (!templateUi || isTemplate || !useTemplateFallback || fallbackUsable);

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const result = await fileUploadService.uploadMultiple(files);
      if (result.uploaded.length > 0) {
        setAttachedFiles(prev => [...prev, ...result.uploaded]);
      }
      if (result.errors.length > 0) {
        alert(`Erro ao enviar: ${result.errors.map(e => e.filename).join(', ')}`);
      }
    } catch (err) {
      alert('Erro ao enviar arquivo: ' + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeFile = (idx) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const openPromoModal = async (subtitle) => {
    try {
      const plansRes = await subscriptionService.getPlans();
      const allPlans = plansRes.data || plansRes;
      // Filter only regular (non-combo) plans with price > 0
      setPromoPlans(allPlans.filter(p => p.product !== 'combo' && p.price_brl > 0));
    } catch {
      setPromoPlans([]);
    }
    setPromoSubtitle(subtitle);
    setShowPromoModal(true);
  };

  const handleRecurrenceTypeChange = (type) => {
    if (type === 'recurring' && !canCreateRecurring) {
      openPromoModal('Follow-ups recorrentes estao disponiveis a partir do plano Pro. Faca upgrade para desbloquear.');
      return;
    }
    setRecurrenceType(type);
  };

  const handleSchedule = async () => {
    if (!canSchedule) return;

    // Frontend guard: check count limit for one-time sends
    if (effectiveRecurrence === 'once' && isAtCountLimit) {
      openPromoModal(`Voce atingiu o limite de ${limits.max_allowed} follow-ups do plano ${(limits.tier || 'sales_free').replace('sales_', '').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}. Faca upgrade para criar mais.`);
      return;
    }

    setScheduling(true);
    try {
      const filesPayload = attachedFiles.map(f => ({
        url: f.url,
        type: f.type,
        filename: f.filename,
        mimeType: f.mimeType,
      }));

      if (isBroadcast) {
        // Build the dispatch payload that matches LabelDispatchRequest on the
        // backend. The MessageData schema accepts the same shape used in the
        // single-message flow, plus we wrap schedule into ScheduleConfig.
        const isImmediate = dispatchTiming === 'now';
        // Quando "agora", o backend reescreve schedule.start_date/send_time
        // para o instante presente — mandamos placeholders validos so para
        // satisfazer o Pydantic (ScheduleConfig exige formato HH:MM/YYYY-MM-DD).
        const effectiveSendTime = isImmediate ? '00:00' : sendTime;
        const effectiveSendDate = isImmediate ? sendDate : sendDate;
        const effectiveRecurrenceForPayload = isImmediate ? 'once' : effectiveRecurrence;

        const channelIsBusiness =
          broadcastChannel === 'wa_business' || broadcastChannel === 'both';
        const channelIsPersonal =
          broadcastChannel === 'wa_personal' || broadcastChannel === 'both';

        // Em broadcast a mensagem pode ser custom (texto+anexos),
        // ai_generated (instrucao livre — agente resolve por contato no envio)
        // ou template (mensagem Meta pre-aprovada — so wa_business). Em
        // ai_generated/template os campos text/files sao ignorados pelo backend.
        const broadcastMessage = isAi
          ? {
              order: 1,
              send_time: effectiveSendTime,
              text: null,
              files: [],
              message_type: 'ai_generated',
              instruction: instruction.trim(),
              use_conversation_context: useConversationContext,
              allow_agent_skip: allowAgentSkip,
              context_message_limit: 15,
              force_response: forceResponse,
            }
          : isTemplate
            ? {
                order: 1,
                send_time: effectiveSendTime,
                text: null,
                files: [],
                message_type: 'template',
                template_name: mainTemplateName.trim(),
                template_language: mainTemplateLanguage || 'pt_BR',
                ...(templateUi && newTemplateAnalysis
                  ? automationTemplateFields(newTemplateAnalysis, mainTemplateValues, scheduleCaps)
                  : {
                    template_params: mainTemplateBodyParams.length
                      ? mainTemplateParams.map((v) => (v || '').trim())
                      : undefined,
                  }),
              }
            : {
                order: 1,
                send_time: effectiveSendTime,
                text: messageText.trim() || null,
                files: filesPayload,
                message_type: 'custom',
              };

        const payload = {
          name: `Disparo — ${labelInfo?.name || 'etapa'}`,
          messages: [broadcastMessage],
          schedule: {
            send_time: effectiveSendTime,
            recurrence: effectiveRecurrenceForPayload,
            recurrence_days: effectiveRecurrenceForPayload === 'weekly' ? recurrenceDays : [],
            recurrence_day_of_month:
              effectiveRecurrenceForPayload === 'monthly' ? recurrenceDayOfMonth : null,
            start_date: effectiveSendDate,
            end_date: null,
            business_hours_only: !!businessWindow.business_hours_only,
            weekdays: businessWindow.weekdays || [],
          },
          channel_filter: broadcastChannel,
          dispatch_mode: isImmediate ? 'immediate' : 'scheduled',
          // anti-bloqueio so faz sentido quando o canal alvo inclui wa_personal
          anti_block: channelIsPersonal && antiBlock,
          typing_simulation: channelIsPersonal && typingSimulation,
        };

        if (channelIsBusiness && useTemplateFallback && fallbackTemplateName.trim()) {
          payload.business_config = {
            fallback_template_name: fallbackTemplateName.trim(),
            fallback_template_language: fallbackTemplateLanguage || 'pt_BR',
            retry_interval_hours: 6,
            max_retries: 3,
          };
        }

        await labelDispatchService.dispatch(labelInfo.id, payload);
      } else {
        const payload = {
          phone: contactPhone,
          contact_name: contactName || '',
          // text/files so para custom; ai_generated e template nao usam.
          text: !isAi && !isTemplate ? (messageText.trim() || null) : null,
          files: !isAi && !isTemplate ? filesPayload : [],
          send_date: sendDate,
          send_time: sendTime,
          recurrence: effectiveRecurrence,
          recurrence_days: effectiveRecurrence === 'weekly' ? recurrenceDays : [],
          recurrence_day_of_month:
            effectiveRecurrence === 'monthly' ? recurrenceDayOfMonth : null,
          // Template Meta so via wa_business — forcamos o canal aqui.
          channel: isTemplate ? 'wa_business' : (channel || 'wa_personal'),
          message_type: messageType,
          cancel_on_reply: cancelOnReply,
          business_hours_only: !!businessWindow.business_hours_only,
          weekdays: businessWindow.weekdays || [],
        };
        if (isAi) {
          payload.instruction = instruction.trim();
          payload.use_conversation_context = useConversationContext;
          payload.allow_agent_skip = allowAgentSkip;
          payload.context_message_limit = 15;
          payload.force_response = forceResponse;
        }
        if (isTemplate) {
          payload.template_name = mainTemplateName.trim();
          payload.template_language = mainTemplateLanguage || 'pt_BR';
          if (templateUi && newTemplateAnalysis) {
            Object.assign(
              payload,
              automationTemplateFields(newTemplateAnalysis, mainTemplateValues, scheduleCaps),
            );
          } else if (mainTemplateBodyParams.length) {
            payload.template_params = mainTemplateParams.map((v) => (v || '').trim());
          }
        }
        // Fallback template (mensagem padrao quando a livre/IA nao puder ser
        // entregue). So no wa_business, e nao faz sentido junto com Template
        // como modo principal.
        if (isBusinessChannel && !isTemplate && useTemplateFallback && fallbackTemplateName.trim()) {
          payload.fallback_template_name = fallbackTemplateName.trim();
          payload.fallback_template_language = fallbackTemplateLanguage || 'pt_BR';
        }
        await followupService.quickCreate(payload);
      }

      setSuccess(true);
      if (onScheduled) onScheduled();
      setTimeout(() => {
        onClose();
        // Reset state after close
        setMessageText('');
        setAttachedFiles([]);
        setRecurrenceType('once');
        setCancelOnReply(false);
        setBusinessWindow({ business_hours_only: false, weekdays: [] });
        setRecurrence('daily');
        setRecurrenceDays([]);
        setMessageType('custom');
        setInstruction('');
        setMainTemplateName('');
        setMainTemplateLanguage('pt_BR');
        setMainTemplateValues(emptyTemplateValues());
        setUseConversationContext(true);
        setForceResponse(true);
        setWindowStatus(null);
        setDispatchTiming('now');
        setAntiBlock(true);
        setTypingSimulation(false);
        setShowAdvanced(false);
        setUseTemplateFallback(false);
        setFallbackTemplateName('');
        setFallbackTemplateLanguage('pt_BR');
        setSuccess(false);
      }, 1500);
    } catch (err) {
      // Handle 403 from backend (plan limit)
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && (detail?.code === 'followup_limit_reached' || detail?.code === 'ai_generated_requires_pro')) {
        openPromoModal(detail.message || 'Faca upgrade para desbloquear mais follow-ups.');
      } else {
        alert('Erro ao agendar: ' + (typeof detail === 'string' ? detail : detail?.message || err.message));
      }
    } finally {
      setScheduling(false);
    }
  };

  const recurrenceLabel = effectiveRecurrence === 'once' ? 'Uma vez' :
    effectiveRecurrence === 'daily' ? 'Diariamente' :
    effectiveRecurrence === 'weekly' ? 'Semanalmente' : 'Mensalmente';

  /** Format YYYY-MM-DD to DD/MM/YYYY */
  const formatDateBR = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return dateStr || '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  // Helpers de passo-a-passo compartilhados entre broadcast e single-mode.
  const stepStyles = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 6 },
    head: { display: 'flex', alignItems: 'center', gap: 8 },
    num: {
      width: 22, height: 22, borderRadius: '50%',
      backgroundColor: colors.accentPrimary, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, flexShrink: 0,
    },
    title: { margin: 0, fontSize: 14, fontWeight: 600, color: colors.textPrimary },
    hint: { margin: '0 0 4px 30px', fontSize: 12, color: colors.textSecondary, lineHeight: 1.45 },
    body: { marginLeft: 30, display: 'flex', flexDirection: 'column', gap: 10 },
  };
  // disabledReason: opção que não existe no canal fica desabilitada com o
  // motivo no lugar da descrição (não some).
  const choiceCard = ({ icon: Icon, label: lbl, desc, selected, locked, onClick, disabledReason }) => (
    <button
      type="button"
      onClick={locked || disabledReason ? undefined : onClick}
      disabled={locked || Boolean(disabledReason)}
      title={disabledReason || undefined}
      style={{
        flex: 1, textAlign: 'left',
        padding: '12px 14px', borderRadius: 10,
        backgroundColor: selected ? `${colors.accentPrimary}10` : colors.bgSecondary,
        border: `1.5px solid ${selected ? colors.accentPrimary : colors.borderColor}`,
        cursor: locked || disabledReason ? 'not-allowed' : 'pointer',
        display: 'flex', gap: 10, alignItems: 'flex-start',
        transition: 'all 0.15s', position: 'relative',
        opacity: locked ? 0.7 : disabledReason ? 0.55 : 1,
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        backgroundColor: selected ? colors.accentPrimary : `${colors.accentPrimary}15`,
        color: selected ? '#fff' : colors.accentPrimary,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: selected ? colors.accentPrimary : colors.textPrimary,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {locked && <Lock size={11} />}
          {lbl}
        </div>
        <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.35 }}>
          {disabledReason || desc}
        </div>
      </div>
      {locked && (
        <span style={{
          position: 'absolute', top: 6, right: 8,
          backgroundColor: colors.accentPrimary, color: '#fff',
          fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
        }}>PRO</span>
      )}
    </button>
  );

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex',
        alignItems: isDrawer ? 'stretch' : 'center',
        justifyContent: isDrawer ? 'flex-end' : 'center',
        padding: isDrawer ? 0 : '20px',
      }}
    >
      <div style={{
        backgroundColor: colors.bgPrimary,
        borderRadius: isDrawer ? 0 : '16px',
        width: isDrawer ? 720 : '100%',
        maxWidth: isDrawer ? '100%' : '480px',
        overflow: 'hidden',
        boxShadow: isDrawer
          ? '-8px 0 32px rgba(0,0,0,0.25)'
          : '0 20px 60px rgba(0,0,0,0.3)',
        height: isDrawer ? '100%' : 'auto',
        maxHeight: isDrawer ? '100%' : '90vh',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
          flexShrink: 0,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isBroadcast ? (
                <>
                  <Zap size={18} color={colors.accentPrimary} /> Disparo por etapa
                </>
              ) : (
                <>
                  <Clock size={18} color={colors.accentPrimary} /> Agendar mensagem
                </>
              )}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
              {isBroadcast ? (
                <>
                  {labelInfo?.color && (
                    <span style={{
                      width: 10, height: 10, borderRadius: 3,
                      backgroundColor: labelInfo.color, display: 'inline-block',
                    }} />
                  )}
                  <span>Etapa: {labelInfo?.name || '—'}</span>
                  <span style={{ color: colors.textTertiary }}>•</span>
                  <Users size={12} />
                  {previewLoading ? (
                    <span>contando…</span>
                  ) : (
                    <span>{recipientPreview?.count ?? 0} contatos</span>
                  )}
                  {!previewLoading && (recipientPreview?.excluded_outside_wallet || 0) > 0 && (
                    <span
                      style={{ color: colors.textTertiary }}
                      title="Você dispara só para a sua carteira: contatos com outro vendedor como responsável não recebem."
                    >
                      · {recipientPreview.excluded_outside_wallet} de outros vendedores ficam de fora
                    </span>
                  )}
                </>
              ) : (
                <>Para: {contactName || contactPhone}</>
              )}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary,
            padding: '8px', borderRadius: '8px',
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto', flex: 1 }}>
          {success ? (
            <div style={{
              textAlign: 'center', padding: '32px 0', display: 'flex',
              flexDirection: 'column', alignItems: 'center', gap: '12px',
            }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '50%',
                backgroundColor: '#22c55e20', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <Check size={24} color="#22c55e" />
              </div>
              <span style={{ fontSize: '15px', fontWeight: 600, color: colors.textPrimary }}>
                {isBroadcast
                  ? (immediateBroadcast ? 'Campanha iniciada!' : 'Disparo agendado!')
                  : 'Mensagem agendada!'}
              </span>
              <span style={{ fontSize: '13px', color: colors.textSecondary }}>
                {isBroadcast && recipientPreview?.count != null
                  ? `${recipientPreview.count} contatos serao notificados`
                  : ''}
                {isBroadcast && recipientPreview?.count != null ? <br /> : null}
                {immediateBroadcast
                  ? 'Os envios começaram agora.'
                  : (recurrenceType === 'once'
                      ? `Envio em ${formatDateBR(sendDate)} as ${sendTime}`
                      : `${recurrenceLabel} as ${sendTime}, a partir de ${formatDateBR(sendDate)}`)
                }
              </span>
            </div>
          ) : isBroadcast ? (
            <>
              {/* === BROADCAST (campanha por etiqueta) — passo-a-passo === */}
              {!connections.loading && !connections.qr && !connections.business && (
                <div style={{
                  fontSize: 12, color: '#b45309',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <AlertTriangle size={13} />
                  Nenhuma conexão WhatsApp ativa — conecte na tela de Conexões.
                </div>
              )}

              {/* Step 1 — Quando enviar */}
              <section style={stepStyles.wrap}>
                <div style={stepStyles.head}>
                  <span style={stepStyles.num}>1</span>
                  <h4 style={stepStyles.title}>Quando você quer disparar?</h4>
                </div>
                <p style={stepStyles.hint}>
                  Comece agora ou escolha uma data e horário para começar.
                </p>
                <div style={{ ...stepStyles.body, flexDirection: 'row', gap: 8 }}>
                  {choiceCard({
                    icon: Send,
                    label: 'Agora',
                    desc: 'Os envios começam assim que a campanha for criada.',
                    selected: dispatchTiming === 'now',
                    onClick: () => setDispatchTiming('now'),
                  })}
                  {choiceCard({
                    icon: Calendar,
                    label: 'Agendar',
                    desc: 'Escolha data e horário (e recorrência, se quiser).',
                    selected: dispatchTiming === 'schedule',
                    onClick: () => setDispatchTiming('schedule'),
                  })}
                </div>
              </section>

              {/* Step 2 — Tipo de mensagem (3 cards) */}
              <section style={stepStyles.wrap}>
                <div style={stepStyles.head}>
                  <span style={stepStyles.num}>2</span>
                  <h4 style={stepStyles.title}>Que tipo de mensagem?</h4>
                </div>
                <p style={stepStyles.hint}>
                  Escreva você mesmo, use um template aprovado da Meta ou peça pra IA criar personalizada para cada contato.
                </p>
                <div style={{
                  ...stepStyles.body, flexDirection: 'row', gap: 8, flexWrap: 'wrap',
                }}>
                  {choiceCard({
                    icon: Send,
                    label: 'Eu escrevo',
                    desc: 'Você digita exatamente o que vai chegar.',
                    selected: messageType === 'custom',
                    onClick: () => setMessageType('custom'),
                  })}
                  {/* Template Meta — so quando o canal alvo inclui wa_business */}
                  {(broadcastChannel === 'wa_business' || broadcastChannel === 'both' || templateUi) && choiceCard({
                    icon: FileText,
                    label: 'Template aprovado',
                    desc: 'Mensagem pré-aprovada da Meta. Funciona sempre.',
                    selected: messageType === 'template',
                    onClick: () => setMessageType('template'),
                    disabledReason: broadcastChannel === 'wa_business' || broadcastChannel === 'both'
                      ? null : 'Só existe no WhatsApp Business.',
                  })}
                  {choiceCard({
                    icon: Sparkles,
                    label: 'IA gera para mim',
                    desc: 'A IA monta a mensagem na hora, por contato.',
                    selected: isAi,
                    locked: !canCreateAiGenerated,
                    onClick: () => {
                      if (!canCreateAiGenerated) {
                        openPromoModal('Mensagens com IA estao disponiveis a partir do plano Pro. Faca upgrade para desbloquear.');
                        return;
                      }
                      setMessageType('ai_generated');
                    },
                  })}
                </div>
              </section>

              {/* Step 3 — Conteudo dinamico (texto / template / IA) */}
              <section style={stepStyles.wrap}>
                <div style={stepStyles.head}>
                  <span style={stepStyles.num}>3</span>
                  <h4 style={stepStyles.title}>
                    {isTemplate ? 'Escolha o template aprovado'
                      : isAi ? 'O que a IA deve dizer?'
                      : 'Digite a mensagem'}
                  </h4>
                </div>
                <p style={stepStyles.hint}>
                  {isTemplate
                    ? 'Templates Meta são pré-aprovados e podem ser enviados a qualquer momento.'
                    : isAi
                      ? 'Quanto mais específico, melhor o resultado.'
                      : 'Esta é exatamente a mensagem que cada contato vai receber.'}
                </p>
                <div style={stepStyles.body}>
              {isBroadcast && isAi ? (
                <>
                  {!instruction.trim() && (
                    <div>
                      <div style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 6, fontWeight: 500 }}>
                        Quer alguma orientação rápida? Comece por uma destas:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {[
                          { label: 'Apresentar produto', text: 'Apresente nossa solução de forma breve e amigável usando o primeiro nome do contato. Destaque o principal benefício e termine com uma pergunta convidando o contato a saber mais.' },
                          { label: 'Convidar para reunião', text: 'Convide o contato para uma reunião de 15 minutos esta semana. Use o primeiro nome dele, seja direto e gentil. Pergunte qual o melhor dia.' },
                          { label: 'Follow-up pós-compra', text: 'Faça um follow-up amigável com o contato após a compra. Use o primeiro nome, agradeça pela confiança e pergunte se está tudo certo com o produto/serviço.' },
                          { label: 'Reativar contato frio', text: 'Reative o contato de forma leve e sem pressão. Use o primeiro nome, mencione que faz um tempo desde a última conversa e pergunte se ele ainda tem interesse.' },
                        ].map((s) => (
                          <button
                            key={s.label}
                            type="button"
                            onClick={() => setInstruction(s.text)}
                            style={{
                              padding: '4px 10px', borderRadius: 999, fontSize: 11,
                              backgroundColor: `${colors.accentPrimary}12`,
                              color: colors.accentPrimary,
                              border: `1px solid ${colors.accentPrimary}30`,
                              cursor: 'pointer', fontWeight: 500,
                            }}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <textarea
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    placeholder="Ex: Faça uma saudação personalizada usando o primeiro nome do contato. Pergunte se ele tem dúvida sobre o produto X."
                    rows={4}
                    maxLength={2000}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 8,
                      border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                      color: colors.textPrimary, fontSize: 14, outline: 'none',
                      resize: 'vertical', minHeight: 88, fontFamily: 'inherit', boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: colors.textTertiary }}>
                      Mínimo 10 caracteres.
                    </span>
                    <span style={{ fontSize: 11, color: colors.textTertiary }}>
                      {instruction.length}/2000
                    </span>
                  </div>

                  {/* Configurações avançadas da IA — colapsavel */}
                  <div style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 10 }}>
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(v => !v)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        color: colors.textSecondary, fontSize: 12, fontWeight: 500,
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ShieldCheck size={13} /> Configurações avançadas da IA
                      </span>
                      {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    {showAdvanced && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                        <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                          <input
                            type="checkbox"
                            checked={useConversationContext}
                            onChange={(e) => setUseConversationContext(e.target.checked)}
                            style={{ marginTop: 2 }}
                          />
                          <span>
                            <strong>Considerar contexto da conversa</strong>
                            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                              A IA terá acesso às últimas 15 mensagens trocadas com cada contato.
                            </div>
                          </span>
                        </label>
                        <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                          <input
                            type="checkbox"
                            checked={forceResponse}
                            onChange={(e) => setForceResponse(e.target.checked)}
                            style={{ marginTop: 2 }}
                          />
                          <span>
                            <strong>Forçar a IA a responder sempre</strong>
                            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                              Desmarcado (recomendado em massa), a IA PULA quem está em
                              atendimento humano ou quando julgar inadequado — o contato
                              aparece como "pulado" no histórico. Marcado, envia para todos.
                            </div>
                          </span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Disclaimer WA Business — discreto */}
                  {(broadcastChannel === 'wa_business' || broadcastChannel === 'both') && (
                    <div style={{
                      display: 'flex', gap: 6, alignItems: 'flex-start',
                      fontSize: 11, color: colors.textTertiary, lineHeight: 1.4,
                      padding: '4px 2px',
                    }}>
                      <AlertTriangle size={11} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>
                        Mensagens da IA só são entregues no WhatsApp Business se o contato respondeu nas últimas 24 horas.
                      </span>
                    </div>
                  )}
                </>
              ) : isBroadcast && isTemplate && templateUi ? (
                <TemplateMessageField
                  templates={availableTemplates}
                  loading={templatesLoading}
                  templateName={mainTemplateName}
                  templateLanguage={mainTemplateLanguage}
                  onTemplateChange={(tpl) => {
                    setMainTemplateName(tpl?.name || '');
                    if (tpl?.language) setMainTemplateLanguage(tpl.language);
                  }}
                  values={mainTemplateValues}
                  onValuesChange={setMainTemplateValues}
                  capabilities={scheduleCaps}
                  colors={colors}
                  previewContact={recipientPreview?.sample?.[0] || null}
                />
              ) : isBroadcast && isTemplate ? (
                <>
                  <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>
                    Template aprovado
                  </label>
                  <select
                    value={mainTemplateName}
                    onChange={(e) => {
                      const tpl = availableTemplates.find(t => t.name === e.target.value);
                      setMainTemplateName(e.target.value);
                      if (tpl?.language) setMainTemplateLanguage(tpl.language);
                      setMainTemplateParams([]);
                    }}
                    disabled={templatesLoading}
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 8,
                      border: `1px solid ${colors.borderColor}`,
                      backgroundColor: colors.bgPrimary,
                      color: colors.textPrimary, fontSize: 14, outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">
                      {templatesLoading
                        ? 'Carregando...'
                        : availableTemplates.length === 0
                          ? 'Nenhum template aprovado — crie em Templates'
                          : 'Selecione um template...'}
                    </option>
                    {availableTemplates.map(t => (
                      <option key={t.name} value={t.name}>
                        {t.name}{t.status && t.status !== 'APPROVED' ? ` (${t.status})` : ''}
                      </option>
                    ))}
                  </select>
                  <TemplateParamsFields
                    params={mainTemplateBodyParams}
                    values={mainTemplateParams}
                    onChange={setMainTemplateParams}
                    bodyText={mainTemplateBodyText}
                    colors={colors}
                  />
                </>
              ) : isBroadcast ? (
              <>
              {/* Message Text (broadcast custom) */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textPrimary, marginBottom: '6px' }}>
                  Mensagem
                </label>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Escreva sua mensagem... use {{primeiro_nome}} para personalizar"
                  maxLength={4096}
                  rows={3}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '8px',
                    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary, fontSize: '14px', outline: 'none',
                    resize: 'vertical', minHeight: '72px', maxHeight: '160px',
                    fontFamily: 'inherit', boxSizing: 'border-box',
                  }}
                />
                <VariableChips
                  style={{ marginTop: 6 }}
                  onInsert={(token) =>
                    setMessageText((prev) => (prev ? `${prev}${prev.endsWith(' ') || prev.endsWith('\n') ? '' : ' '}${token}` : token))
                  }
                />
                {/* Proteções do disparo — o cliente pedia isso claro na UI (08/08) */}
                <div style={{
                  marginTop: 8, padding: '8px 10px', borderRadius: 8,
                  backgroundColor: colors.bgPrimary,
                  border: `1px solid ${colors.borderColor}`,
                  fontSize: 11, color: colors.textSecondary, lineHeight: 1.5,
                }}>
                  <strong style={{ color: colors.textPrimary }}>Proteções automáticas:</strong>{' '}
                  o envio é espaçado para proteger seu número; nos canais Meta só
                  entra na janela de 24h (fora dela usa o template padrão, se
                  configurado); e cada contato fica com status próprio no
                  histórico do disparo (enviado, pulado ou falha).
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={fileUploadService.getAcceptString()}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || attachedFiles.length >= 10}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: colors.accentPrimary, fontSize: '12px', padding: '2px 6px',
                        borderRadius: '4px', opacity: uploading ? 0.5 : 1,
                      }}
                    >
                      {uploading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Paperclip size={13} />}
                      {uploading ? 'Enviando...' : 'Anexar arquivo'}
                    </button>
                  </div>
                  <span style={{ fontSize: '11px', color: colors.textTertiary }}>
                    {messageText.length}/4096
                  </span>
                </div>
              </div>

              {/* Attached Files */}
              {attachedFiles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {attachedFiles.map((file, idx) => {
                    const Icon = FILE_ICONS[file.type] || FileText;
                    return (
                      <div key={idx} style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '4px 8px', borderRadius: '6px',
                        backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
                        fontSize: '11px', color: colors.textSecondary,
                      }}>
                        <Icon size={12} />
                        <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {file.filename}
                        </span>
                        <button
                          onClick={() => removeFile(idx)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '0 2px', display: 'flex' }}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              </>
              ) : null}

              {/* Card de mensagem padrao (fallback) — disponivel quando o
                  canal alvo inclui wa_business e o modo principal nao e
                  Template (que ja e o template em si). */}
              {(broadcastChannel === 'wa_business' || broadcastChannel === 'both') && !isTemplate && (
                <div style={{
                  padding: 12, borderRadius: 10,
                  backgroundColor: colors.bgSecondary,
                  border: `1px solid ${colors.borderColor}`,
                }}>
                  <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                    <input
                      type="checkbox"
                      checked={useTemplateFallback}
                      onChange={(e) => setUseTemplateFallback(e.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <strong>Enviar uma mensagem padrão se não puder ser entregue</strong>
                      <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.4 }}>
                        O WhatsApp Business só aceita mensagens livres se o cliente respondeu nas últimas 24 horas. Fora disso, enviamos a mensagem pré-aprovada que você escolher.
                      </div>
                    </span>
                  </label>
                  {useTemplateFallback && (
                    <div style={{ marginTop: 10 }}>
                      <label style={{ display: 'block', fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>
                        Escolha a mensagem padrão
                      </label>
                      {templateUi ? (
                        <TemplatePicker
                          templates={availableTemplates}
                          loading={templatesLoading}
                          value={fallbackTemplateName}
                          language={fallbackTemplateLanguage}
                          onChange={(tpl) => {
                            setFallbackTemplateName(tpl?.name || '');
                            if (tpl?.language) setFallbackTemplateLanguage(tpl.language);
                          }}
                          capabilities={fallbackCaps}
                          colors={colors}
                          placeholder="Selecione uma mensagem"
                        />
                      ) : (
                      <select
                        value={fallbackTemplateName}
                        onChange={(e) => {
                          const tpl = fallbackTemplates.find(t => t.name === e.target.value);
                          setFallbackTemplateName(e.target.value);
                          if (tpl?.language) setFallbackTemplateLanguage(tpl.language);
                        }}
                        disabled={templatesLoading}
                        style={{
                          width: '100%', padding: '8px 10px', borderRadius: 8,
                          border: `1px solid ${colors.borderColor}`,
                          backgroundColor: colors.bgPrimary,
                          color: colors.textPrimary, fontSize: 13, outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      >
                        <option value="">
                          {templatesLoading
                            ? 'Carregando...'
                            : fallbackTemplates.length === 0
                              ? 'Nenhuma mensagem pré-aprovada sem variáveis — crie em Templates'
                              : 'Selecione uma mensagem...'}
                        </option>
                        {/* Fallback automático: só templates SEM variáveis */}
                        {fallbackTemplates.map(t => (
                          <option key={t.name} value={t.name}>
                            {t.name}{t.status && t.status !== 'APPROVED' ? ` (${t.status})` : ''}
                          </option>
                        ))}
                      </select>
                      )}
                    </div>
                  )}
                </div>
              )}
                </div>
              </section>

              {/* Step 4 — Data / horario / recorrencia (apenas quando agendado) */}
              {isBroadcast && dispatchTiming === 'schedule' && (
                <section style={stepStyles.wrap}>
                  <div style={stepStyles.head}>
                    <span style={stepStyles.num}>4</span>
                    <h4 style={stepStyles.title}>Quando exatamente?</h4>
                  </div>
                  <p style={stepStyles.hint}>
                    Define quando o envio começa e se repete.
                  </p>
                  <div style={{ ...stepStyles.body, flexDirection: 'row', gap: 8 }}>
                    {choiceCard({
                      icon: Send,
                      label: 'Uma única vez',
                      desc: 'Envia só nesta data e horário.',
                      selected: recurrenceType === 'once',
                      onClick: () => handleRecurrenceTypeChange('once'),
                    })}
                    {choiceCard({
                      icon: Repeat,
                      label: 'De forma recorrente',
                      desc: 'Repete em intervalos definidos.',
                      selected: recurrenceType === 'recurring',
                      locked: !canCreateRecurring,
                      onClick: () => handleRecurrenceTypeChange('recurring'),
                    })}
                  </div>

                  <div style={{ ...stepStyles.body, marginTop: 4 }}>
                    {recurrenceType === 'recurring' && (
                      <div style={{
                        display: 'flex', flexDirection: 'column', gap: 10,
                        padding: 12, borderRadius: 10,
                        backgroundColor: `${colors.accentPrimary}06`,
                        border: `1px solid ${colors.accentPrimary}15`,
                      }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textSecondary }}>
                          Com que frequência?
                        </label>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {['daily', 'weekly', 'monthly'].map(opt => (
                            <button
                              key={opt}
                              onClick={() => setRecurrence(opt)}
                              style={{
                                flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 12,
                                fontWeight: 500, cursor: 'pointer', border: 'none',
                                backgroundColor: recurrence === opt ? colors.accentPrimary : colors.bgPrimary,
                                color: recurrence === opt ? '#fff' : colors.textSecondary,
                              }}
                            >
                              {opt === 'daily' ? 'Diário' : opt === 'weekly' ? 'Semanal' : 'Mensal'}
                            </button>
                          ))}
                        </div>
                        {recurrence === 'weekly' && (
                          <div>
                            <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>
                              Dias da semana
                            </label>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {WEEKDAYS.map(day => {
                                const isSelected = recurrenceDays.includes(day.value);
                                return (
                                  <button
                                    key={day.value}
                                    onClick={() => {
                                      setRecurrenceDays(prev =>
                                        isSelected
                                          ? prev.filter(d => d !== day.value)
                                          : [...prev, day.value].sort()
                                      );
                                    }}
                                    style={{
                                      width: 38, height: 38, borderRadius: '50%', fontSize: 11,
                                      fontWeight: 600, cursor: 'pointer', border: 'none',
                                      backgroundColor: isSelected ? colors.accentPrimary : colors.bgPrimary,
                                      color: isSelected ? '#fff' : colors.textSecondary,
                                    }}
                                  >
                                    {day.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {recurrence === 'monthly' && (
                          <div>
                            <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>
                              Dia do mês
                            </label>
                            <select
                              value={recurrenceDayOfMonth}
                              onChange={(e) => setRecurrenceDayOfMonth(parseInt(e.target.value))}
                              style={{
                                padding: '8px 12px', borderRadius: 8,
                                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                                color: colors.textPrimary, fontSize: 13, outline: 'none',
                              }}
                            >
                              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                <option key={d} value={d}>Dia {d}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textSecondary, marginBottom: 4 }}>
                          <Calendar size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          {recurrenceType === 'recurring' ? 'Começar em' : 'Data'}
                        </label>
                        <input
                          type="date"
                          value={sendDate}
                          onChange={(e) => setSendDate(e.target.value)}
                          style={{
                            width: '100%', padding: '10px 12px', borderRadius: 8,
                            border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                            color: colors.textPrimary, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textSecondary, marginBottom: 4 }}>
                          <Clock size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                          Horário
                        </label>
                        <input
                          type="time"
                          value={sendTime}
                          onChange={(e) => setSendTime(e.target.value)}
                          style={{
                            width: '100%', padding: '10px 12px', borderRadius: 8,
                            border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                            color: colors.textPrimary, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {featureFlags.automation_business_window && (
                <section style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 4 }}>
                  <BusinessWindowFields
                    compact
                    weekdayFormat="index"
                    colors={colors}
                    value={businessWindow}
                    onChange={setBusinessWindow}
                  />
                </section>
              )}

              {/* Opcoes avancadas (broadcast) — anti-bloqueio + simular digitacao
                  do WA QR. Fallback HSM ja vive no card compartilhado acima. */}
              {(broadcastChannel === 'wa_personal' || broadcastChannel === 'both') && (
                <section style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14 }}>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(v => !v)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      color: colors.textPrimary, fontSize: 13, fontWeight: 600,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ShieldCheck size={14} color={colors.accentPrimary} />
                      Opções avançadas de envio
                    </span>
                    {showAdvanced ? <ChevronUp size={16} color={colors.textSecondary} /> : <ChevronDown size={16} color={colors.textSecondary} />}
                  </button>
                  {showAdvanced && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                      <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                        <input
                          type="checkbox"
                          checked={antiBlock}
                          onChange={(e) => setAntiBlock(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <strong>Espaçar envios (anti-bloqueio)</strong>
                          <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                            Recomendado para WhatsApp QR — disparos acima de 50 por dia aumentam o risco de banimento. Intervalo aleatório de 8 a 15s entre envios.
                          </div>
                        </span>
                      </label>
                      <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                        <input
                          type="checkbox"
                          checked={typingSimulation}
                          onChange={(e) => setTypingSimulation(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <strong>Simular digitação antes do envio</strong>
                          <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                            Mostra o status "digitando..." por alguns segundos antes da mensagem.
                          </div>
                        </span>
                      </label>
                    </div>
                  )}
                </section>
              )}
            </>
          ) : (
            <>
              {/* === SINGLE-MODE — fluxo passo-a-passo === */}
              {(() => {
                const stepStyles = {
                  wrap: { display: 'flex', flexDirection: 'column', gap: 6 },
                  head: { display: 'flex', alignItems: 'center', gap: 8 },
                  num: {
                    width: 22, height: 22, borderRadius: '50%',
                    backgroundColor: colors.accentPrimary, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                  },
                  title: {
                    margin: 0, fontSize: 14, fontWeight: 600,
                    color: colors.textPrimary,
                  },
                  hint: {
                    margin: '0 0 4px 30px', fontSize: 12,
                    color: colors.textSecondary, lineHeight: 1.45,
                  },
                  body: { marginLeft: 30, display: 'flex', flexDirection: 'column', gap: 10 },
                };

                const choiceCard = ({ icon: Icon, label, desc, selected, locked, onClick, disabledReason }) => (
                  <button
                    type="button"
                    onClick={locked || disabledReason ? undefined : onClick}
                    disabled={locked || Boolean(disabledReason)}
                    title={disabledReason || undefined}
                    style={{
                      flex: 1, textAlign: 'left',
                      padding: '12px 14px', borderRadius: 10,
                      backgroundColor: selected ? `${colors.accentPrimary}10` : colors.bgSecondary,
                      border: `1.5px solid ${selected ? colors.accentPrimary : colors.borderColor}`,
                      cursor: locked || disabledReason ? 'not-allowed' : 'pointer',
                      display: 'flex', gap: 10, alignItems: 'flex-start',
                      transition: 'all 0.15s',
                      position: 'relative',
                      opacity: locked ? 0.7 : disabledReason ? 0.55 : 1,
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      backgroundColor: selected ? colors.accentPrimary : `${colors.accentPrimary}15`,
                      color: selected ? '#fff' : colors.accentPrimary,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon size={16} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600,
                        color: selected ? colors.accentPrimary : colors.textPrimary,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        {locked && <Lock size={11} />}
                        {label}
                      </div>
                      <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.35 }}>
                        {disabledReason || desc}
                      </div>
                    </div>
                    {locked && (
                      <span style={{
                        position: 'absolute', top: 6, right: 8,
                        backgroundColor: colors.accentPrimary, color: '#fff',
                        fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                      }}>PRO</span>
                    )}
                  </button>
                );

                const isBusiness = channel === 'wa_business';

                return (
                  <>
                    {/* Step 1 — Como quer enviar */}
                    <section style={stepStyles.wrap}>
                      <div style={stepStyles.head}>
                        <span style={stepStyles.num}>1</span>
                        <h4 style={stepStyles.title}>Como você quer enviar?</h4>
                      </div>
                      <div style={{
                        ...stepStyles.body,
                        flexDirection: 'row', gap: 8, flexWrap: 'wrap',
                      }}>
                        {choiceCard({
                          icon: Send,
                          label: 'Eu escrevo',
                          desc: 'Você digita exatamente o que vai chegar.',
                          selected: messageType === 'custom',
                          onClick: () => setMessageType('custom'),
                        })}
                        {/* Template Meta — so wa_business */}
                        {(isBusiness || templateUi) && choiceCard({
                          icon: FileText,
                          label: 'Template aprovado',
                          desc: 'Mensagem pré-aprovada da Meta. Funciona sempre, mesmo fora da janela 24h.',
                          selected: messageType === 'template',
                          onClick: () => setMessageType('template'),
                          disabledReason: isBusiness ? null : 'Só existe no WhatsApp Business.',
                        })}
                        {choiceCard({
                          icon: Sparkles,
                          label: 'IA gera para mim',
                          desc: 'A IA monta a mensagem personalizada na hora.',
                          selected: isAi,
                          locked: !canCreateAiGenerated,
                          onClick: () => {
                            if (!canCreateAiGenerated) {
                              openPromoModal('Mensagens com IA estao disponiveis a partir do plano Pro. Faca upgrade para desbloquear.');
                              return;
                            }
                            setMessageType('ai_generated');
                          },
                        })}
                      </div>
                    </section>

                    {/* Step 2 — Conteúdo */}
                    <section style={stepStyles.wrap}>
                      <div style={stepStyles.head}>
                        <span style={stepStyles.num}>2</span>
                        <h4 style={stepStyles.title}>
                          {isTemplate ? 'Escolha o template aprovado'
                            : isAi ? 'O que a IA deve dizer?'
                            : 'Digite a mensagem que você quer enviar'}
                        </h4>
                      </div>

                      <div style={stepStyles.body}>
                        {isAi ? (
                          <>
                            {/* Sugestões — somente quando vazio (chips de orientação rápida) */}
                            {!instruction.trim() && (
                              <div>
                                <div style={{
                                  fontSize: 11, color: colors.textSecondary,
                                  marginBottom: 6, fontWeight: 500,
                                }}>
                                  Quer alguma orientação rápida? Comece por uma destas:
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                  {[
                                    { label: 'Apresentar produto', text: 'Apresente nossa solução de forma breve e amigável usando o primeiro nome do contato. Destaque o principal benefício e termine com uma pergunta convidando o contato a saber mais.' },
                                    { label: 'Convidar para reunião', text: 'Convide o contato para uma reunião de 15 minutos esta semana. Use o primeiro nome dele, seja direto e gentil. Pergunte qual o melhor dia.' },
                                    { label: 'Follow-up pós-compra', text: 'Faça um follow-up amigável com o contato após a compra. Use o primeiro nome, agradeça pela confiança e pergunte se está tudo certo com o produto/serviço.' },
                                    { label: 'Reativar contato frio', text: 'Reative o contato de forma leve e sem pressão. Use o primeiro nome, mencione que faz um tempo desde a última conversa e pergunte se ele ainda tem interesse.' },
                                  ].map((s) => (
                                    <button
                                      key={s.label}
                                      type="button"
                                      onClick={() => setInstruction(s.text)}
                                      style={{
                                        padding: '4px 10px', borderRadius: 999, fontSize: 11,
                                        backgroundColor: `${colors.accentPrimary}12`,
                                        color: colors.accentPrimary,
                                        border: `1px solid ${colors.accentPrimary}30`,
                                        cursor: 'pointer', fontWeight: 500,
                                      }}
                                    >
                                      {s.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            <textarea
                              value={instruction}
                              onChange={(e) => setInstruction(e.target.value)}
                              placeholder="Ex: Faça uma saudação personalizada usando o primeiro nome do contato. Pergunte se ele tem dúvida sobre o produto X."
                              rows={4}
                              maxLength={2000}
                              style={{
                                width: '100%', padding: '10px 12px', borderRadius: '8px',
                                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                                color: colors.textPrimary, fontSize: '14px', outline: 'none',
                                resize: 'vertical', minHeight: '88px', fontFamily: 'inherit', boxSizing: 'border-box',
                              }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: 11, color: colors.textTertiary }}>
                                Mínimo 10 caracteres.
                              </span>
                              <span style={{ fontSize: 11, color: colors.textTertiary }}>
                                {instruction.length}/2000
                              </span>
                            </div>

                            {/* Disclaimer WA Business — discreto */}
                            {isBusiness && (
                              <div style={{
                                display: 'flex', gap: 6, alignItems: 'flex-start',
                                fontSize: 11, color: colors.textTertiary, lineHeight: 1.4,
                                padding: '4px 2px',
                              }}>
                                <AlertTriangle size={11} style={{ flexShrink: 0, marginTop: 2 }} />
                                <span>
                                  Mensagens da IA só são entregues no WhatsApp Business se o contato respondeu nas últimas 24 horas.
                                  {windowStatus && !windowStatus.is_open && ' Este contato está fora dessa janela agora.'}
                                </span>
                              </div>
                            )}

                            {/* Configurações avançadas — colapsavel */}
                            <div style={{ borderTop: `1px solid ${colors.borderColor}`, paddingTop: 10 }}>
                              <button
                                type="button"
                                onClick={() => setShowAdvanced(v => !v)}
                                style={{
                                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                  color: colors.textSecondary, fontSize: 12, fontWeight: 500,
                                }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <ShieldCheck size={13} />
                                  Configurações avançadas
                                </span>
                                {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              </button>

                              {showAdvanced && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                                  <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                                    <input
                                      type="checkbox"
                                      checked={useConversationContext}
                                      onChange={(e) => setUseConversationContext(e.target.checked)}
                                      style={{ marginTop: 2 }}
                                    />
                                    <span>
                                      <strong>Considerar contexto da conversa</strong>
                                      <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                                        A IA terá acesso às últimas 15 mensagens trocadas com este contato.
                                      </div>
                                    </span>
                                  </label>

                                  {/* Forcar resposta — quando marcado (default)
                                      a IA nao pode usar a tool de ignorar.
                                      Quando desmarcado, derivamos
                                      allow_agent_skip=true e a IA ganha
                                      autonomia para pular. */}
                                  <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                                    <input
                                      type="checkbox"
                                      checked={forceResponse}
                                      onChange={(e) => setForceResponse(e.target.checked)}
                                      style={{ marginTop: 2 }}
                                    />
                                    <span>
                                      <strong>Forçar a IA a responder sempre</strong>
                                      <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
                                        Recomendado. Se desmarcar, a IA pode decidir não enviar caso julgue inadequado.
                                      </div>
                                    </span>
                                  </label>
                                </div>
                              )}
                            </div>
                          </>
                        ) : isTemplate && templateUi ? (
                          <TemplateMessageField
                            templates={availableTemplates}
                            loading={templatesLoading}
                            templateName={mainTemplateName}
                            templateLanguage={mainTemplateLanguage}
                            onTemplateChange={(tpl) => {
                              setMainTemplateName(tpl?.name || '');
                              if (tpl?.language) setMainTemplateLanguage(tpl.language);
                            }}
                            values={mainTemplateValues}
                            onValuesChange={setMainTemplateValues}
                            capabilities={scheduleCaps}
                            colors={colors}
                            mode="single"
                            contactKey={contactPhone || ''}
                            contactName={contactName || ''}
                          />
                        ) : isTemplate ? (
                          <>
                            <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>
                              Template aprovado
                            </label>
                            <select
                              value={mainTemplateName}
                              onChange={(e) => {
                                const tpl = availableTemplates.find(t => t.name === e.target.value);
                                setMainTemplateName(e.target.value);
                                if (tpl?.language) setMainTemplateLanguage(tpl.language);
                              }}
                              disabled={templatesLoading}
                              style={{
                                width: '100%', padding: '10px 12px', borderRadius: 8,
                                border: `1px solid ${colors.borderColor}`,
                                backgroundColor: colors.bgPrimary,
                                color: colors.textPrimary, fontSize: 14, outline: 'none',
                                boxSizing: 'border-box',
                              }}
                            >
                              <option value="">
                                {templatesLoading
                                  ? 'Carregando...'
                                  : availableTemplates.length === 0
                                    ? 'Nenhum template aprovado — crie em Templates'
                                    : 'Selecione um template...'}
                              </option>
                              {availableTemplates.map(t => (
                                <option key={t.name} value={t.name}>
                                  {t.name}{t.status && t.status !== 'APPROVED' ? ` (${t.status})` : ''}
                                </option>
                              ))}
                            </select>
                            <TemplateParamsFields
                              params={mainTemplateBodyParams}
                              values={mainTemplateParams}
                              onChange={setMainTemplateParams}
                              bodyText={mainTemplateBodyText}
                              colors={colors}
                            />
                          </>
                        ) : (
                          <>
                            <textarea
                              value={messageText}
                              onChange={(e) => setMessageText(e.target.value)}
                              placeholder="Escreva sua mensagem... use {{primeiro_nome}} para personalizar"
                              maxLength={4096}
                              rows={3}
                              style={{
                                width: '100%', padding: '10px 12px', borderRadius: 8,
                                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                                color: colors.textPrimary, fontSize: 14, outline: 'none',
                                resize: 'vertical', minHeight: 72, maxHeight: 160,
                                fontFamily: 'inherit', boxSizing: 'border-box',
                              }}
                            />
                            <VariableChips
                              style={{ marginTop: 6 }}
                              onInsert={(token) =>
                                setMessageText((prev) => (prev ? `${prev}${prev.endsWith(' ') || prev.endsWith('\n') ? '' : ' '}${token}` : token))
                              }
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  multiple
                                  accept={fileUploadService.getAcceptString()}
                                  onChange={handleFileSelect}
                                  style={{ display: 'none' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={uploading || attachedFiles.length >= 10}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: colors.accentPrimary, fontSize: 12, padding: '2px 6px',
                                    borderRadius: 4, opacity: uploading ? 0.5 : 1,
                                  }}
                                >
                                  {uploading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Paperclip size={13} />}
                                  {uploading ? 'Enviando...' : 'Anexar arquivo'}
                                </button>
                              </div>
                              <span style={{ fontSize: 11, color: colors.textTertiary }}>
                                {messageText.length}/4096
                              </span>
                            </div>
                            {attachedFiles.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {attachedFiles.map((file, idx) => {
                                  const Icon = FILE_ICONS[file.type] || FileText;
                                  return (
                                    <div key={idx} style={{
                                      display: 'flex', alignItems: 'center', gap: 6,
                                      padding: '4px 8px', borderRadius: 6,
                                      backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
                                      fontSize: 11, color: colors.textSecondary,
                                    }}>
                                      <Icon size={12} />
                                      <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {file.filename}
                                      </span>
                                      <button
                                        onClick={() => removeFile(idx)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '0 2px', display: 'flex' }}
                                      >
                                        <Trash2 size={11} />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        )}

                        {/* Card de mensagem padrão (fallback) — disponível para
                            Texto e IA quando wa_business. Não aparece quando o
                            modo principal já é Template (não faz sentido). */}
                        {isBusiness && !isTemplate && (
                          <div style={{
                            padding: 12, borderRadius: 10,
                            backgroundColor: colors.bgSecondary,
                            border: `1px solid ${colors.borderColor}`,
                          }}>
                            <label style={{ display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.textPrimary }}>
                              <input
                                type="checkbox"
                                checked={useTemplateFallback}
                                onChange={(e) => setUseTemplateFallback(e.target.checked)}
                                style={{ marginTop: 2 }}
                              />
                              <span>
                                <strong>Enviar uma mensagem padrão se não puder ser entregue</strong>
                                <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.4 }}>
                                  O WhatsApp Business só aceita mensagens livres se o cliente respondeu nas últimas 24 horas. Fora disso, enviamos a mensagem pré-aprovada que você escolher.
                                </div>
                              </span>
                            </label>
                            {useTemplateFallback && (
                              <div style={{ marginTop: 10 }}>
                                <label style={{ display: 'block', fontSize: 11, color: colors.textSecondary, marginBottom: 4 }}>
                                  Escolha a mensagem padrão
                                </label>
                                {templateUi ? (
                                  <TemplatePicker
                                    templates={availableTemplates}
                                    loading={templatesLoading}
                                    value={fallbackTemplateName}
                                    language={fallbackTemplateLanguage}
                                    onChange={(tpl) => {
                                      setFallbackTemplateName(tpl?.name || '');
                                      if (tpl?.language) setFallbackTemplateLanguage(tpl.language);
                                    }}
                                    capabilities={fallbackCaps}
                                    colors={colors}
                                    placeholder="Selecione uma mensagem"
                                  />
                                ) : (
                                <select
                                  value={fallbackTemplateName}
                                  onChange={(e) => {
                                    const tpl = availableTemplates.find(t => t.name === e.target.value);
                                    setFallbackTemplateName(e.target.value);
                                    if (tpl?.language) setFallbackTemplateLanguage(tpl.language);
                                  }}
                                  disabled={templatesLoading}
                                  style={{
                                    width: '100%', padding: '8px 10px', borderRadius: 8,
                                    border: `1px solid ${colors.borderColor}`,
                                    backgroundColor: colors.bgPrimary,
                                    color: colors.textPrimary, fontSize: 13, outline: 'none',
                                    boxSizing: 'border-box',
                                  }}
                                >
                                  <option value="">
                                    {templatesLoading
                                      ? 'Carregando...'
                                      : fallbackTemplates.length === 0
                                        ? 'Nenhuma mensagem pré-aprovada sem variáveis — crie em Templates'
                                        : 'Selecione uma mensagem...'}
                                  </option>
                                  {/* Fallback automático: só templates SEM variáveis */}
                                  {fallbackTemplates.map(t => (
                                    <option key={t.name} value={t.name}>
                                      {t.name}{t.status && t.status !== 'APPROVED' ? ` (${t.status})` : ''}
                                    </option>
                                  ))}
                                </select>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Step 3 — Quando enviar */}
                    <section style={stepStyles.wrap}>
                      <div style={stepStyles.head}>
                        <span style={stepStyles.num}>3</span>
                        <h4 style={stepStyles.title}>Quando você quer disparar?</h4>
                      </div>
                      <div style={{ ...stepStyles.body, flexDirection: 'row', gap: 8 }}>
                        {choiceCard({
                          icon: Send,
                          label: 'Uma única vez',
                          desc: 'Envia só nesta data e horário.',
                          selected: recurrenceType === 'once',
                          onClick: () => handleRecurrenceTypeChange('once'),
                        })}
                        {choiceCard({
                          icon: Repeat,
                          label: 'De forma recorrente',
                          desc: 'Repete em intervalos definidos.',
                          selected: recurrenceType === 'recurring',
                          locked: !canCreateRecurring,
                          onClick: () => handleRecurrenceTypeChange('recurring'),
                        })}
                      </div>

                      <div style={{ ...stepStyles.body, marginTop: 4 }}>
                        {/* Frequencia (Diario/Semanal/Mensal) vem ANTES de
                            data/hora para o usuario perceber a opcao escolhida
                            e nao ficar oculta logo abaixo dos inputs. */}
                        {recurrenceType === 'recurring' && (
                          <div style={{
                            display: 'flex', flexDirection: 'column', gap: 10,
                            padding: 12, borderRadius: 10,
                            backgroundColor: `${colors.accentPrimary}06`,
                            border: `1px solid ${colors.accentPrimary}15`,
                          }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textSecondary }}>
                              Com que frequência?
                            </label>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {['daily', 'weekly', 'monthly'].map(opt => (
                                <button
                                  key={opt}
                                  onClick={() => setRecurrence(opt)}
                                  style={{
                                    flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 12,
                                    fontWeight: 500, cursor: 'pointer', border: 'none',
                                    backgroundColor: recurrence === opt ? colors.accentPrimary : colors.bgPrimary,
                                    color: recurrence === opt ? '#fff' : colors.textSecondary,
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  {opt === 'daily' ? 'Diário' : opt === 'weekly' ? 'Semanal' : 'Mensal'}
                                </button>
                              ))}
                            </div>

                            {recurrence === 'weekly' && (
                              <div>
                                <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>
                                  Dias da semana
                                </label>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  {WEEKDAYS.map(day => {
                                    const isSelected = recurrenceDays.includes(day.value);
                                    return (
                                      <button
                                        key={day.value}
                                        onClick={() => {
                                          setRecurrenceDays(prev =>
                                            isSelected
                                              ? prev.filter(d => d !== day.value)
                                              : [...prev, day.value].sort()
                                          );
                                        }}
                                        style={{
                                          width: 38, height: 38, borderRadius: '50%', fontSize: 11,
                                          fontWeight: 600, cursor: 'pointer', border: 'none',
                                          backgroundColor: isSelected ? colors.accentPrimary : colors.bgPrimary,
                                          color: isSelected ? '#fff' : colors.textSecondary,
                                          transition: 'all 0.15s',
                                        }}
                                      >
                                        {day.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {recurrence === 'monthly' && (
                              <div>
                                <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>
                                  Dia do mês
                                </label>
                                <select
                                  value={recurrenceDayOfMonth}
                                  onChange={(e) => setRecurrenceDayOfMonth(parseInt(e.target.value))}
                                  style={{
                                    padding: '8px 12px', borderRadius: 8,
                                    border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                                    color: colors.textPrimary, fontSize: 13, outline: 'none',
                                  }}
                                >
                                  {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                    <option key={d} value={d}>Dia {d}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: 12 }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textSecondary, marginBottom: 4 }}>
                              <Calendar size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                              {recurrenceType === 'recurring' ? 'Começar em' : 'Data'}
                            </label>
                            <input
                              type="date"
                              value={sendDate}
                              onChange={(e) => setSendDate(e.target.value)}
                              style={{
                                width: '100%', padding: '10px 12px', borderRadius: 8,
                                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                                color: colors.textPrimary, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                              }}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textSecondary, marginBottom: 4 }}>
                              <Clock size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                              Horário
                            </label>
                            <input
                              type="time"
                              value={sendTime}
                              onChange={(e) => setSendTime(e.target.value)}
                              style={{
                                width: '100%', padding: '10px 12px', borderRadius: 8,
                                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgPrimary,
                                color: colors.textPrimary, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </section>

                    {featureFlags.followup_cancel_on_reply && (
                      <label style={{
                        display: 'flex', gap: 8, cursor: 'pointer', fontSize: 12,
                        color: colors.textPrimary, marginLeft: 30, marginTop: 4,
                      }}>
                        <input
                          type="checkbox"
                          checked={cancelOnReply}
                          onChange={(e) => setCancelOnReply(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <strong>Cancelar se o cliente responder antes</strong>
                          <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2, lineHeight: 1.4 }}>
                            Se o contato mandar mensagem antes do horário
                            agendado, este envio é cancelado automaticamente e
                            aparece como "Cancelado: o cliente respondeu".
                          </div>
                        </span>
                      </label>
                    )}
                    {featureFlags.automation_business_window && (
                      <div style={{ marginLeft: 30 }}>
                        <BusinessWindowFields
                          compact
                          weekdayFormat="index"
                          colors={colors}
                          value={businessWindow}
                          onChange={setBusinessWindow}
                        />
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px',
            padding: '14px 20px', borderTop: `1px solid ${colors.borderColor}`,
            flexShrink: 0,
          }}>
            <button onClick={onClose} style={{
              padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
              backgroundColor: 'transparent', color: colors.textSecondary,
              border: `1px solid ${colors.borderColor}`, cursor: 'pointer',
            }}>
              Cancelar
            </button>
            <button
              onClick={handleSchedule}
              disabled={!canSchedule || scheduling}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                backgroundColor: colors.accentPrimary, color: 'white',
                border: 'none', cursor: !canSchedule || scheduling ? 'default' : 'pointer',
                opacity: !canSchedule || scheduling ? 0.5 : 1,
              }}
            >
              {scheduling
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : (isBroadcast ? <Zap size={14} /> : <Clock size={14} />)}
              {scheduling
                ? (isBroadcast ? 'Disparando...' : 'Agendando...')
                : (isBroadcast
                    ? (immediateBroadcast ? 'Disparar agora' : 'Agendar disparo')
                    : 'Agendar')}
            </button>
          </div>
        )}
      </div>

      {/* PromoModal for plan upgrade */}
      <PromoModal
        isOpen={showPromoModal}
        onClose={() => setShowPromoModal(false)}
        plans={promoPlans}
        title="Faca upgrade para desbloquear"
        subtitle={promoSubtitle}
      />
    </div>
  );
};

export default ScheduleMessageModal;
